import { isPlatformBrowser, Location } from '@angular/common';
import { PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { IAuction, IStatus } from '../interfaces';
import { AuctionService } from '../services';
import { AuctionItemSearchService, AuctionSearchResponse } from '../services/auction-item-search.service';
import { shouldLog } from '../utils/logging';

export interface HomeAuctionsData {
  hits: IAuction[];
  promoted: IAuction[];
  /**
   * Total open auctions on the search service. When it is ≤ `hits.length` the
   * browser has the whole list already and skips its own `/search/auctions`
   * call; otherwise it fetches only the remaining pages (same page size).
   */
  nbHits: number;
  /**
   * True when `hits` are page 0 of `/search/auctions` (so the browser can page
   * on from page 1). False for the Firestore fallback, whose list the browser
   * replaces with a full search-service fetch as before.
   */
  fromSearch: boolean;
}

/** SSR → browser handoff of the search-engine first-paint list and compact hero subset. */
export const HOME_AUCTIONS_KEY = makeStateKey<HomeAuctionsData>('HomeAuctions');

/**
 * Page size shared by the SSR first paint and any browser follow-up pages
 * (`AuctionsComponent` pages 1…N with this same size when `nbHits` exceeds it).
 */
export const HOME_RESOLVER_SIZE = 100;
const HERO_CLOSED_CUTOFF_MS = 4 * 60 * 60 * 1000;
/** Hard cap so a slow search service can never hold up server rendering. */
const SSR_SEARCH_TIMEOUT_MS = 3000;

function promotedFromHits(hits: IAuction[]): IAuction[] {
  const cutoff = Date.now() - HERO_CLOSED_CUTOFF_MS;
  return (hits ?? []).filter(
    (auction) => !!auction.promoted && auction.status === IStatus.OPEN && auction.time_end > cutoff,
  );
}

function searchData(response: AuctionSearchResponse): HomeAuctionsData {
  return {
    hits: response.hits,
    // Keeps the frontend compatible during a rolling API deploy: an older
    // search service has no promoted key, so seed from its first page only.
    promoted: response.promoted ?? promotedFromHits(response.hits),
    nbHits: response.nbHits,
    fromSearch: true,
  };
}

async function firestoreFallback(auctionService: AuctionService): Promise<HomeAuctionsData> {
  const hits = await auctionService.getInitialOpenAuctions();
  return { hits, promoted: promotedFromHits(hits), nbHits: hits.length, fromSearch: false };
}

/**
 * Homepage/search-route resolver. The first-paint list comes from the search
 * microservice's `/search/auctions` endpoint — much faster than the Firestore
 * query it replaces, and the same source (and order: `last_item_time_end`
 * ascending) as every later browser-side list load, so hydration and the
 * deferred full-list fetch never reorder the grid. `includePromoted=true`
 * carries the compact, unpaginated hero data in this same request. The
 * Firestore path (`AuctionService.getInitialOpenAuctions`) remains the fallback when the
 * search service errors or times out, and still serves `?tags=` deep links.
 */
export async function resolveHomeAuctions(): Promise<HomeAuctionsData> {
  const platformId = inject(PLATFORM_ID);
  const transferState = inject(TransferState);
  const location = inject(Location);
  const auctionService = inject(AuctionService);
  const searchService = inject(AuctionItemSearchService);

  if (location.path().includes('tags=')) {
    return firestoreFallback(auctionService);
  }

  if (isPlatformBrowser(platformId)) {
    if (transferState.hasKey(HOME_AUCTIONS_KEY)) {
      return transferState.get(HOME_AUCTIONS_KEY, { hits: [], promoted: [], nbHits: 0, fromSearch: false });
    }
    // SPA navigation or degraded SSR — fetch the same first batch directly.
    try {
      const res = await searchService.searchAuctions({
        query: '',
        page: 0,
        size: HOME_RESOLVER_SIZE,
        includePromoted: true,
      });
      return searchData(res);
    } catch (error) {
      if (shouldLog()) {
        console.error('[home-auctions-resolver] /search/auctions failed in browser', error);
      }
      return firestoreFallback(auctionService);
    }
  }

  try {
    const res = await searchService.searchAuctions({
      query: '',
      page: 0,
      size: HOME_RESOLVER_SIZE,
      includePromoted: true,
      timeoutMs: SSR_SEARCH_TIMEOUT_MS,
    });
    const data = searchData(res);
    transferState.set(HOME_AUCTIONS_KEY, data);
    return data;
  } catch (error) {
    if (shouldLog()) {
      console.error('[home-auctions-resolver] /search/auctions failed during SSR', error);
    }
    // Old Firestore path: sets its own TransferState payload and, on timeout,
    // the SsrDegraded marker so server.ts serves the render uncached.
    return firestoreFallback(auctionService);
  }
}
